import { Spinner, Title4 } from '@common/components';
import { BlockSelectField } from '@common/components/FormElements';
import { AvailabilityForm as AvailabilityFormComponent } from '@features/DynamicPackagesConfigurationWizard/components/AvailabilityForm/AvailabilityForm';
import { useGetSpaPackageConfigGroups } from '@features/spa';
import { useTranslations } from '@lang';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import { AvailabilityForm, PackagesFormValues, SelectedExperienceType } from '../../../types';
import {
  DividerLine,
  DropdownGridWrapper,
  DropdownWrapper,
  SpaAvailabilityWrapper,
  SubtitleWrapper,
} from './ExperienceTimeslotSelection.styles';
import { TitleWrapper } from '../SingleOptionExperienceTimeslotSelection/SingleOptionExperienceTimeslotSelection.styles';
import { usePackageConfiguration } from '../../../contexts';

type Props = {
  experiences: { optionGroupId: number; options: SelectedExperienceType[] };
  configurationGroups: ConfigurationGroupType[];
  index: number;
  experienceDate: string;
  guestIndex?: number;
};

export const MultipleOptionsExperienceTimeslotSelection: React.FC<Props> = ({
  experiences,
  configurationGroups,
  index,
  experienceDate,
  guestIndex,
}) => {
  const { property, spaPackageId, spaOpenTime, spaCloseTime, totalGuestAmount } =
    usePackageConfiguration();
  const numberOfGuests = totalGuestAmount;

  const { data: configGroups, isFetching } = useGetSpaPackageConfigGroups(
    configurationGroups,
    property.id,
    Number(spaPackageId),
    moment(experienceDate).format('YYYY-MM-DD'),
    numberOfGuests,
    spaOpenTime,
    spaCloseTime
  );

  const selectedExperienceOptionIds = experiences.options.map((opt) => opt.optionId);
  const allOptions = configGroups.flatMap((conf) => conf.options);

  const selectableOptions = allOptions.filter((option) =>
    selectedExperienceOptionIds.includes(option.id)
  );

  const { t } = useTranslations();
  const { watch, setValue } = useFormContext<PackagesFormValues>();
  const delayAfter = watch(`selectedExperiences.${index}.delayAfter`);
  const [selectedExperience, setSelectedExperience] =
    useState<Partial<SelectedExperienceType> | null>(null);
  const selectedExperiences = watch('selectedExperiences');

  const guestsHaveDifferentTimes = watch('experiences.guestsHaveDifferentTimes');
  const minimumTimeslotQuantity = guestsHaveDifferentTimes ? 1 : numberOfGuests;
  const dayNumber = experiences.options[0]?.dayNumber;
  const selectableOptionsFormatted = selectableOptions.map((opt) => ({
    label: opt?.name || '',
    value: String(opt?.id) || '',
  }));

  const guestOption = watch(
    `experiences.guestOptions.${Number(guestIndex)}.timeslots.${dayNumber}.${index}`
  );

  const selectedExperienceValue = (selectableOptionsFormatted || []).find(
    (opt) => Number(opt.value) === guestOption?.optionId
  );

  useEffect(() => {
    const experience = (selectedExperiences || [])?.find(
      (exp) => Number(exp.optionId) === Number(selectedExperienceValue?.value)
    );
    if (selectedExperienceValue && experience) {
      setSelectedExperience({
        id: experience.id,
        optionId: Number(selectedExperienceValue.value),
        type: experience.type,
      });
    }
  }, []);

  const option = selectedExperience
    ? selectableOptions.find((option) => option.id === selectedExperience.optionId)
    : null;

  return selectableOptions ? (
    <>
      <Spinner isActive={isFetching} isBackgroundDisabled={true} />
      <SpaAvailabilityWrapper
        data-testid={`multiple-options-selection-guest-${guestIndex ?? 0}-group-${
          experiences.optionGroupId
        }`}
      >
        <TitleWrapper>
          <Title4>{t('overnightPackage.spa.options')}</Title4>
          <DividerLine />
        </TitleWrapper>
        <SubtitleWrapper>{t('spa.dropdown.subtitle')}</SubtitleWrapper>
        <DropdownGridWrapper>
          <DropdownWrapper>
            <BlockSelectField
              options={selectableOptionsFormatted}
              onChange={(val) => {
                const selectedExperience = (selectedExperiences || [])?.find(
                  (exp) => Number(exp.optionId) === Number(val.value)
                );
                if (selectedExperience) {
                  setSelectedExperience({
                    id: selectedExperience.id,
                    optionId: Number(val.value),
                    type: selectedExperience.type,
                  });
                  setValue(
                    `experiences.guestOptions.${Number(
                      guestIndex
                    )}.timeslots.${dayNumber}.${index}`,
                    null as unknown as AvailabilityForm
                  );
                }
              }}
              placeholder={t('spa.toggle.select')}
              value={selectedExperienceValue}
              useNativeDropdownOnMobile={false}
              isSearchable={false}
              menuPlacement={'auto'}
              noOptionsMessage={() => t('spa.noOptionsAvailable')}
            />
          </DropdownWrapper>
        </DropdownGridWrapper>
        <DropdownGridWrapper>
          <DropdownWrapper>
            {!isFetching &&
              (selectedExperience?.id && selectedExperience.type ? (
                <AvailabilityFormComponent
                  id={Number(selectedExperience.id)}
                  entityType={selectedExperience.type}
                  timeslots={option?.timeslots || []}
                  namePrefix={`experiences.guestOptions.${
                    guestIndex || guestIndex === 0 ? guestIndex : 0
                  }.timeslots.${dayNumber}.${index}`}
                  guestIndex={guestIndex || guestIndex === 0 ? guestIndex : 0}
                  delayAfter={delayAfter}
                  dayNumber={dayNumber}
                  experienceIndex={index}
                  minimumTimeslotQuantity={minimumTimeslotQuantity}
                />
              ) : (
                <BlockSelectField options={[]} onChange={() => {}} placeholder={'-'} isDisabled />
              ))}
          </DropdownWrapper>
        </DropdownGridWrapper>
      </SpaAvailabilityWrapper>
    </>
  ) : null;
};
